import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@1trading/react';
import { useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { CHART_SERIES_COLORS, formatEuroTooltip, renderLegendLabel, TOOLTIP_CONTENT_STYLE, TOOLTIP_ITEM_STYLE } from '@/components/charts/recharts-theme';
import { StatsCard } from '@/components/ui/stats-card';
import { useInstantStatistics } from '@/hooks/queries/instant-trade/use-instant-statistics';
import { getErrorMessage } from '@/lib/api/error';
import { formatDate, formatEuros } from '@/utils/format';

const CHART_HEIGHT = 300;
const DONUT_INNER_RADIUS = 60;
const DONUT_OUTER_RADIUS = 100;

const InstantDashboardPage = () => {
  const { data, isPending, isError, error, refetch } = useInstantStatistics();

  const donutData = useMemo(
    () => (data ? Object.entries(data.marketVolumes).map(([market, volumes]) => ({ name: market, value: Number.parseFloat(volumes.volume) })) : []),
    [data],
  );

  const subtitle = data ? `Last updated: ${formatDate(data.lastUpdated)}` : undefined;

  return (
    <div className="flex flex-col gap-6">
      {isError ? (
        // Also covers the 404 'No statistics data' empty-cache response
        <div className="flex items-center gap-3">
          <span className="text-sm text-red-500">Failed to load: {getErrorMessage(error)}</span>
          <Button onClick={() => refetch()} size="sm" variant="outline">
            Retry
          </Button>
        </div>
      ) : null}
      {isError && !data ? null : (
        <>
          <div className="grid gap-4 lg:grid-cols-3">
            <StatsCard isLoading={isPending} label="24 Hour Volume" subtitle={subtitle} value={data ? formatEuros(data.totalVolume) : null} />
            <StatsCard isLoading={isPending} label="24 Hour Total Trades" subtitle={subtitle} value={data ? data.totalTrades.toString() : null} />
            <StatsCard isLoading={isPending} label="24 Hour Fees Earned" subtitle={subtitle} value={data ? formatEuros(data.totalFees) : null} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Volume by Market Pair</CardTitle>
            </CardHeader>
            <CardContent>
              {isPending ? <Skeleton className="w-full" style={{ height: CHART_HEIGHT }} /> : null}
              {data && donutData.length > 0 ? (
                <ResponsiveContainer height={CHART_HEIGHT} width="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" innerRadius={DONUT_INNER_RADIUS} nameKey="name" outerRadius={DONUT_OUTER_RADIUS}>
                      {donutData.map((entry, index) => (
                        <Cell fill={CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]} key={entry.name} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={formatEuroTooltip} itemStyle={TOOLTIP_ITEM_STYLE} />
                    <Legend align="right" formatter={renderLegendLabel} layout="vertical" verticalAlign="middle" />
                  </PieChart>
                </ResponsiveContainer>
              ) : null}
              {data && donutData.length === 0 ? <p className="text-sm text-muted-foreground">No market volume data</p> : null}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
InstantDashboardPage.displayName = 'InstantDashboardPage';

export default InstantDashboardPage;
